// Geometria de tempo do editor edvid — porta de jcutGeom/draftLayout/draftToRendered
// do preview do edvid (assets/preview/app.js ~650–725). Funções puras: nada aqui
// sabe de React, para a aba Corte, a waveform e as marcações usarem a MESMA conta.
import type { EdlRange, JcutTimelineEntry, Region } from '../../../types/videoEdit';

export const LABEL_W = 48; // largura da calha de ícones (px) — o ponto t=0 das faixas
export const MIN_SEG = 0.2; // take mais curto que o trim permite (s)
export const MAX_PPS = 200;

/** Take editável da aba Corte: o range da EDL + o estado do usuário. */
export interface DraftTake extends EdlRange {
  removed: boolean;
  orig: { start: number; end: number; speed: number };
}

export const SPEED_OPTIONS = [1, 1.1, 1.25, 1.5, 1.75, 2];
/** velocidade efetiva do take (1–2) */
export const speedOf = (r: { speed?: number }) => (Number(r.speed) > 1.001 ? Math.min(2, Number(r.speed)) : 1);
export const fmtSpeed = (v: number) => `${v.toFixed(2).replace(/\.?0+$/, '')}×`;

export interface LaidTake extends DraftTake {
  /** início da IMAGEM na timeline de saída */
  out: number;
  /** duração da imagem na saída */
  dur: number;
  /** início / duração do SOM na saída (J-cut: o som chega antes) */
  aout: number;
  adur: number;
  lead: number;
}

export const fmt = (t: number): string => {
  if (!Number.isFinite(t) || t < 0) t = 0;
  const m = Math.floor(t / 60);
  const s = t - m * 60;
  return `${m}:${s.toFixed(2).padStart(5, '0')}`;
};

export const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);

export const normHex = (v: string | null | undefined): string | null => {
  let s = String(v || '').trim().replace(/^#/, '');
  if (/^[0-9a-f]{3}$/i.test(s)) s = s.split('').map(c => c + c).join('');
  return /^[0-9a-f]{6}$/i.test(s) ? `#${s.toLowerCase()}` : null;
};

export function draftFromRanges(ranges: EdlRange[]): DraftTake[] {
  return ranges.map(r => ({ ...r, start: +r.start, end: +r.end, removed: false, orig: { start: +r.start, end: +r.end, speed: speedOf(r) } }));
}

export const takeDirty = (r: DraftTake) => r.removed || r.start !== r.orig.start || r.end !== r.orig.end || speedOf(r) !== r.orig.speed;

/* Geometria do J-cut por take, em segundos. `lead` = quanto o som do take corre à
 * frente da imagem; `tail` = o que foi aparado no fim. São quadros fixos, então
 * sobrevivem ao usuário aparar o take na UI. */
export function jcutGeom(jcut: JcutTimelineEntry[] | null | undefined, i: number, fps: number) {
  const j = jcut?.[i];
  if (!j) return { lead: 0, tail: 0 };
  return {
    lead: Math.max(0, (j.video_start_in_output || 0) - (j.audio_start_in_output || 0)),
    tail: (j.tail_trim_frames || 0) / (fps || 30),
  };
}

/* A timeline do rascunho modela o J-cut, não só soma os ranges: a imagem de cada
 * take é mais curta que o range pelo lead que ela cede mais a cauda aparada. Somar
 * os ranges crus deixava a régua maior que o render e cada take depois do primeiro
 * atrasado pelo lead acumulado. Cada item leva também o posicionamento do SOM
 * (aout/adur), que é o que as faixas A1/A2 desenham. */
export function draftLayout(draft: DraftTake[], jcut: JcutTimelineEntry[] | null | undefined, fps: number): LaidTake[] {
  let t = 0;
  let at = 0;
  return draft.map((r, i) => {
    if (r.removed) return { ...r, out: t, dur: 0, aout: at, adur: 0, lead: 0 };
    const g = jcutGeom(jcut, i, fps);
    // duração na SAÍDA: o take acelerado ocupa (fim − início) ÷ velocidade
    const span = (r.end - r.start) / speedOf(r);
    const adur = Math.max(0, span - g.tail);
    const dur = Math.max(0, adur - g.lead);
    const item: LaidTake = { ...r, out: t, dur, aout: Math.max(0, at - g.lead), adur, lead: g.lead };
    t += dur;
    at = item.aout + adur;
    return item;
  });
}

/** Posições do que ESTÁ renderizado no cut.mp4 (o jcut_timeline do render manda). */
export function renderedLayout(rendered: EdlRange[], jcut: JcutTimelineEntry[] | null | undefined) {
  if (jcut && jcut.length === rendered.length) {
    return rendered.map((r, i) => ({ ...r, out: jcut[i].video_start_in_output, dur: jcut[i].video_duration }));
  }
  let t = 0;
  return rendered.map(r => {
    const dur = (r.end - r.start) / speedOf(r);
    const item = { ...r, out: t, dur };
    t += dur;
    return item;
  });
}

export type RenderedLaid = ReturnType<typeof renderedLayout>[number];

export const layoutTotal = (dl: Array<{ dur: number }>) => dl.reduce((a, r) => a + r.dur, 0);

/** tempo do rascunho → tempo do cut.mp4 (para tocar o render antigo enquanto se edita) */
export function draftToRendered(t: number, dl: LaidTake[], rl: RenderedLaid[], videoDuration: number): number {
  for (let i = dl.length - 1; i >= 0; i--) {
    const d = dl[i];
    if (d.removed || t < d.out) continue;
    const ratio = speedOf(d) / d.orig.speed; // 1s do rascunho = ratio s do render
    const off = Math.min((t - d.out) * ratio, (rl[i]?.dur ?? d.dur) - 0.02);
    return (rl[i]?.out ?? d.out) + Math.max(0, off);
  }
  return Math.min(t, videoDuration);
}

/**
 * Como draftToRendered, mas devolve null onde o rascunho ainda não existe no
 * render (take estendido além do que foi renderizado): a onda fica vazia ali em
 * vez de repetir a última amostra, que desenhava um bloco sólido falso.
 */
export function draftToRenderedStrict(t: number, dl: LaidTake[], rl: RenderedLaid[]): number | null {
  for (let i = dl.length - 1; i >= 0; i--) {
    const d = dl[i];
    if (d.removed || t < d.out) continue;
    const r = rl[i];
    if (!r) return null;
    // o início aparado desloca o conteúdo: aparar para dentro pula material do render,
    // estender para fora cria um trecho que o render ainda não tem
    const mediaOff = (t - d.out) * (speedOf(d) / d.orig.speed) + (d.start - d.orig.start) / d.orig.speed;
    if (mediaOff < 0 || mediaOff > r.dur) return null;
    return r.out + mediaOff;
  }
  return null;
}

/** tempo do cut.mp4 → tempo do rascunho (a agulha durante a reprodução) */
export function renderedToDraft(t: number, dl: LaidTake[], rl: RenderedLaid[]): number {
  for (let i = rl.length - 1; i >= 0; i--) {
    const r = rl[i];
    if (t < r.out) continue;
    if (dl[i]?.removed) return dl[i].out; // tocando material removido → estaciona no slot
    const ratio = dl[i] ? dl[i].orig.speed / speedOf(dl[i]) : 1;
    const off = Math.min((t - r.out) * ratio, dl[i]?.dur ?? r.dur);
    return (dl[i]?.out ?? r.out) + off;
  }
  return t;
}

/** A borda cai DENTRO de uma fala (não num silêncio)? Margem de 30ms nas bordas da região. */
export function edgeInSpeech(regions: Region[] | undefined, t: number): boolean {
  if (!regions?.length) return false;
  return regions.some(r => t > r.start + 0.03 && t < r.end - 0.03);
}

/** Distribui lanes para itens que se sobrepõem (auto-lanes por tipo, como renderChips do edvid). */
export function assignLanes<T extends { start: number; end: number }>(items: T[]): { lanes: number; laneOf: Map<T, number> } {
  const order = [...items].sort((a, b) => a.start - b.start || a.end - b.end);
  const trackEnd: number[] = [];
  const laneOf = new Map<T, number>();
  for (const c of order) {
    let t = trackEnd.findIndex(end => c.start >= end - 1e-6);
    if (t < 0) { t = trackEnd.length; trackEnd.push(0); }
    trackEnd[t] = c.end;
    laneOf.set(c, t);
  }
  return { lanes: Math.max(1, trackEnd.length), laneOf };
}

/** Ímã: devolve o ponto de `points` mais próximo de `t` se ele estiver a menos de `tol` s. */
export function snapTo(t: number, points: number[], tol: number): number {
  let best = t;
  let bestD = tol;
  for (const p of points) {
    const d = Math.abs(p - t);
    if (d < bestD) { bestD = d; best = p; }
  }
  return best;
}
